/**
 * Attribution capture.
 *
 * The first time a visitor lands on the site with UTM or click-id parameters
 * we persist them to sessionStorage so subsequent form submissions on the same
 * session carry full attribution even after the URL has been cleaned up.
 *
 * Captured keys: utm_source, utm_medium, utm_campaign, utm_term, utm_content,
 * gclid, fbclid, gbraid, wbraid, msclkid, ttclid (TikTok), li_fat_id (LinkedIn).
 */

const STORAGE_KEY = "tpp_attribution_v1";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const CLICK_ID_KEYS = ["gclid", "fbclid", "gbraid", "wbraid", "msclkid", "ttclid", "li_fat_id"] as const;

export type Attribution = {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term?: string;
  utm_content?: string;
  click_ids?: Record<string, string>;
  landing_page?: string;
  referrer?: string;
  captured_at?: string;
};

function readStored(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

function writeStored(value: Attribution) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

/**
 * Captures any UTM or click-id parameters from the current URL into sessionStorage.
 * Safe to call on every route change — only writes when there is something new.
 */
export function captureAttributionFromUrl(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const stored = readStored() ?? {
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    click_ids: {},
    referrer: typeof document !== "undefined" ? document.referrer || "" : "",
    landing_page: window.location.pathname + window.location.search,
    captured_at: new Date().toISOString(),
  };

  let dirty = false;
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v && (stored as any)[k] !== v) {
      (stored as any)[k] = v;
      dirty = true;
    }
  }
  const clickIds = { ...(stored.click_ids ?? {}) };
  for (const k of CLICK_ID_KEYS) {
    const v = params.get(k);
    if (v && clickIds[k] !== v) {
      clickIds[k] = v;
      dirty = true;
    }
  }
  if (dirty) {
    stored.click_ids = clickIds;
    writeStored(stored);
  }
}

/**
 * Read UTM parameters. Prefers the current URL, then falls back to the
 * first-touch values we captured into sessionStorage. Always returns the
 * three core fields so existing call sites keep working.
 */
export function getUtmParams() {
  if (typeof window === "undefined") {
    return { utm_source: "", utm_medium: "", utm_campaign: "" };
  }
  const params = new URLSearchParams(window.location.search);
  const stored = readStored();
  return {
    utm_source: params.get("utm_source") || stored?.utm_source || "",
    utm_medium: params.get("utm_medium") || stored?.utm_medium || "",
    utm_campaign: params.get("utm_campaign") || stored?.utm_campaign || "",
  };
}

/**
 * Returns the full attribution payload (UTMs + click IDs + landing page +
 * referrer) for inclusion in lead `metadata`.
 */
export function getAttribution(): Attribution {
  const utm = getUtmParams();
  const stored = readStored();
  return {
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    utm_term: stored?.utm_term,
    utm_content: stored?.utm_content,
    click_ids: stored?.click_ids ?? {},
    landing_page: stored?.landing_page,
    referrer: stored?.referrer,
    captured_at: stored?.captured_at,
  };
}
